import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  REGION_CODES,
  REGION_LABELS,
  COUNTRY_CODES,
  COUNTRY_LABELS,
  departementsForRegion,
  type RegionCode,
  type CountryCode,
} from '@jeraisonne/db/live';
import { Button, Alert, AlertDescription } from '@jeraisonne/ui';
import { supabase } from '@/lib/supabase';

// Colonnes géo (migration 0155) postérieures aux types stale (cf. CLAUDE.md) →
// client générique + typage à la main, comme @/lib/parent-link.
const db = supabase as unknown as SupabaseClient;

interface StudentGeoControlProps {
  userId: string;
  currentCountry: string | null;
  currentRegion: RegionCode | null;
  currentDepartement: string | null;
}

const SELECT_CLASS =
  'border-ink-200 min-h-11 w-full rounded-xl border bg-white px-3 text-sm transition focus:border-primary-400 focus:outline-none';

/**
 * Saisie FACULTATIVE de la géo de l'élève (Pays / Région / Département) depuis
 * « Mon profil » — alimente les segments de classement du Live School. Aucun
 * champ obligatoire. Écriture directe PostgREST (RLS own_update déjà permise,
 * migration 0151), calque de StudentLevelControl.
 */
export function StudentGeoControl({
  userId,
  currentCountry,
  currentRegion,
  currentDepartement,
}: StudentGeoControlProps) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [country, setCountry] = useState<string | null>(currentCountry);
  const [region, setRegion] = useState<RegionCode | null>(currentRegion);
  const [departement, setDepartement] = useState<string | null>(currentDepartement);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const { error } = await db
        .from('student_profiles')
        .update({
          country: country || null,
          region: region || null,
          departement: departement || null,
          geo_updated_at: new Date().toISOString(),
        })
        .eq('id', userId);
      if (error) throw new Error(error.message);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['student-profile', userId] });
      setEditing(false);
    },
  });

  const deptOptions = region ? departementsForRegion(region) : [];

  const summary = [
    country ? (COUNTRY_LABELS[country as CountryCode] ?? country) : null,
    region ? REGION_LABELS[region] : null,
    departement ? `Dépt ${departement}` : null,
  ].filter(Boolean);

  return (
    <div className="space-y-3" data-testid="student-geo-control">
      <div className="flex items-center justify-between gap-3">
        <span className="text-ink-600 text-sm">Localisation</span>
        <div className="flex items-center gap-2">
          <span className="text-ink-900 text-right text-sm font-semibold">
            {summary.length > 0 ? summary.join(' · ') : 'Non renseignée'}
          </span>
          {!editing && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                setCountry(currentCountry);
                setRegion(currentRegion);
                setDepartement(currentDepartement);
                setEditing(true);
              }}
            >
              Modifier
            </Button>
          )}
        </div>
      </div>

      {!editing && (
        <p className="text-ink-400 text-xs italic">
          Facultatif — sert aux classements régionaux du Live School.
        </p>
      )}

      {editing && (
        <div className="border-ink-100 space-y-3 rounded-xl border bg-white p-3">
          <label className="block space-y-1">
            <span className="text-ink-600 text-xs font-medium">Pays</span>
            <select
              className={SELECT_CLASS}
              value={country ?? ''}
              onChange={(e) => setCountry(e.target.value || null)}
            >
              <option value="">Non renseigné</option>
              {COUNTRY_CODES.map((c) => (
                <option key={c} value={c}>
                  {COUNTRY_LABELS[c]}
                </option>
              ))}
            </select>
          </label>

          <label className="block space-y-1">
            <span className="text-ink-600 text-xs font-medium">Région</span>
            <select
              className={SELECT_CLASS}
              value={region ?? ''}
              onChange={(e) => {
                const next = (e.target.value || null) as RegionCode | null;
                setRegion(next);
                // Le département doit appartenir à la région.
                if (
                  next === null ||
                  (departement && !departementsForRegion(next).includes(departement))
                ) {
                  setDepartement(null);
                }
              }}
            >
              <option value="">Non renseignée</option>
              {REGION_CODES.map((r) => (
                <option key={r} value={r}>
                  {REGION_LABELS[r]}
                </option>
              ))}
            </select>
          </label>

          <label className="block space-y-1">
            <span className="text-ink-600 text-xs font-medium">Département</span>
            <select
              className={SELECT_CLASS}
              value={departement ?? ''}
              disabled={!region}
              onChange={(e) => setDepartement(e.target.value || null)}
            >
              <option value="">{region ? 'Non renseigné' : 'Choisis d’abord une région'}</option>
              {deptOptions.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          </label>

          {saveMutation.isError && (
            <Alert variant="error">
              <AlertDescription>
                {(saveMutation.error as Error)?.message ?? 'Une erreur est survenue.'}
              </AlertDescription>
            </Alert>
          )}

          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="w-full sm:w-auto"
              onClick={() => {
                setEditing(false);
                saveMutation.reset();
              }}
              disabled={saveMutation.isPending}
            >
              Annuler
            </Button>
            <Button
              type="button"
              size="sm"
              className="w-full sm:w-auto"
              disabled={saveMutation.isPending}
              onClick={() => saveMutation.mutate()}
            >
              {saveMutation.isPending ? 'Enregistrement…' : 'Enregistrer'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
